<template>
    <el-container class="detail-page account-page">
        <el-header class="account-header">
            <h1>{{$t('account.title')}}</h1>
        </el-header>
        <el-main class="account-main">
            <div v-if="loading && !accountLoaded" class="account-initial-loading" v-loading="true"></div>
            <el-alert v-else-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
            <el-card v-else-if="accountLoaded" class="account-card">
                <header class="account-identity">
                    <div class="logo-control">
                        <div v-if="haveLogo==1" class="logo-row">
                            <div class="logo-preview">
                                <el-image :src="fileInfo.url">
                                    <div slot="placeholder" class="remote-image-loading">
                                        <i class="el-icon-loading"></i>
                                    </div>
                                    <div slot="error" class="logo-image-error">
                                        <i class="el-icon-warning-outline"></i>
                                        <span>{{$t('account.logoUnavailable')}}</span>
                                    </div>
                                </el-image>
                            </div>
                            <div class="logo-actions">
                                <el-upload
                                        :action=fullRequestURL
                                        :show-file-list="false"
                                        :on-success="handleSuccess"
                                        :on-error="handleUploadError"
                                        :before-upload="onBeforeUpload"
                                        :data="uploadData"
                                        accept="image/jpeg,image/png,image/bmp">
                                    <el-button type="primary" plain :loading="attachPhotosStatus">{{$t('common.change')}}</el-button>
                                </el-upload>
                                <el-button class="logo-delete-button" @click="handleRemove()"
                                           :loading="deleteLogoStatus" type="text">{{$t('common.delete')}}
                                </el-button>
                            </div>
                        </div>

                        <div v-else class="logo-empty-row">
                            <el-upload
                                    :action=fullRequestURL
                                    class="avatar-uploader"
                                    :show-file-list="false"
                                    :on-success="handleSuccess"
                                    :on-error="handleUploadError"
                                    :before-upload="onBeforeUpload"
                                    :data="uploadData"
                                    accept="image/jpeg,image/png,image/bmp">
                                <div class="logo-upload-box">
                                    <i class="el-icon-plus avatar-uploader-icon"></i>
                                    <span>{{addLogoText || $t('account.addLogo')}}</span>
                                </div>
                            </el-upload>
                        </div>
                    </div>
                    <div class="profile-details">
                        <div class="account-company-name">{{userCompany.companyName}}</div>
                        <div class="account-email">{{loginInformation.email}}</div>
                    </div>
                </header>
                <div class="account-settings">
                    <div class="connect-section">
                        <div class="connect-title">{{$t('account.connectSocial')}}</div>
                        <div class="connect-row">
                            <button type="button"
                                    class="connect-button connect-google"
                                    :class="{'is-bound': googleBound !== null}"
                                    :disabled="googleBinding || googleBound !== null"
                                    @click.prevent="handleConnectGoogle">
                                <span class="connect-logo" aria-hidden="true">
                                    <svg width="16" height="16" viewBox="0 0 48 48">
                                        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                                        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                                        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                                        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                                    </svg>
                                </span>
                                <span class="connect-label">{{ googleLabel }}</span>
                                <i v-if="googleBinding" class="el-icon-loading connect-state" aria-hidden="true"></i>
                                <svg v-else class="connect-state" :class="{'is-connected': googleBound !== null}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="googleBound !== null" d="m5 12 4 4L19 6"/>
                                    <path v-else d="m9 5 7 7-7 7"/>
                                </svg>
                            </button>
                            <button type="button"
                                    class="connect-button connect-apple"
                                    :class="{'is-bound': appleBound !== null}"
                                    :disabled="appleBinding || appleBound !== null"
                                    @click.prevent="handleConnectApple">
                                <img src="@/static/icon/apple.svg" alt="" class="connect-logo connect-logo-img">
                                <span class="connect-label">{{ appleLabel }}</span>
                                <i v-if="appleBinding" class="el-icon-loading connect-state" aria-hidden="true"></i>
                                <svg v-else class="connect-state" :class="{'is-connected': appleBound !== null}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path v-if="appleBound !== null" d="m5 12 4 4L19 6"/>
                                    <path v-else d="m9 5 7 7-7 7"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                <section class="account-plan-section">
                    <div class="plan-details">
                        <div class="plan-summary-line">
                            <span class="current-plan-name">{{$t('account.currentPlan', {plan: currentPlan})}}</span>
                            <div class="plan-meta">
                                <span v-if="currentPlan !== 'Free' || isSubscriptionExpired" class="plan-expiry">{{$t('account.planActiveUntil', {date: formatEnglishDate1(loginInformation.expiresTime* 1000)})}}</span>
                                <span v-if="currentPlan !== 'Free'" class="plan-share-note">{{$t('account.sharedWithMobile')}}</span>
                            </div>
                        </div>
                        <div v-if="isSubscriptionExpired" class="plan-expired-note">{{$t('account.subscriptionExpired')}}</div>
                        <div class="plan-note">{{$t('account.planNote')}}</div>
                    </div>
                    <el-button class="subscriptionButton" type="success" @click.native.prevent="openSubscriptionPage">
                        {{subscriptionButtonText}}
                    </el-button>
                </section>
            </el-card>
        </el-main>
    </el-container>
</template>

<script>
    import {timest} from '@/utils/validate'
    import {formatEnglishDate} from '@/utils/time'
    import {getCurrentPlanName, isSubscriptionExpired as checkSubscriptionExpired, openFastSpringPage} from '@/utils/subscription'
    import {
        COMPANY_LOGO_FOLDER,
        discardPreparedFileUpload,
        getDownloadV2Url,
        getFileUploadUrl,
        getStoredImageDownloadUrl,
        getUploadRemoteKey,
        prepareFileUpload,
        takeUploadInnerUUID
    } from '@/utils/fileUpload'
    import {
        PROVIDER_GOOGLE,
        PROVIDER_APPLE,
        signInWithGoogle,
        initGoogleSignIn,
        signInWithApple,
        bindLoggedInSocialLogin
    } from '@/utils/socialLogin'
    import {replaceStoredCompany, syncCompanySelection} from '@/utils/company'
    import {reportHandledError} from '@/utils/sentry'


    export default {
        methods: {
            formatEnglishDate1: formatEnglishDate,

            openSubscriptionPage() {
                if (!this.accountLoaded) {
                    return;
                }
                openFastSpringPage(this.currentPlan, this.currentPlan === 'Premium' ? 'Premium' : 'Pro');
            },
            async queryUserInformation() {
                this.loading = true;
                this.accountLoaded = false;
                this.loadError = '';
                try {
                    const responses = await Promise.all([
                        this.$axios.post('get_auth', {params: {}}),
                        this.$axios.post('query_setting_and_company', {params: {}})
                    ]);
                    const authResponse = responses[0];
                    const companyResponse = responses[1];
                    if (authResponse.data.errorCode !== 0 || authResponse.data.objectData == null) {
                        throw new Error('Account query failed');
                    }
                    if (companyResponse.data.errorCode !== 0) {
                        throw new Error('Company query failed');
                    }
                    const selectedData = syncCompanySelection(companyResponse.data);
                    if (selectedData == null) {
                        throw new Error('Selected company data is missing');
                    }
                    this.userCompany = selectedData.company;
                    this.loginInformation = authResponse.data.objectData;
                    this.uploadData.email = this.loginInformation.email;
                    this.googleBound = this.loginInformation.googleBound != null ? this.loginInformation.googleBound : null;
                    this.appleBound = this.loginInformation.appleBound != null ? this.loginInformation.appleBound : null;

                    const companyLogo = authResponse.data.companyLogo;
                    if (companyLogo != null) {
                        this.fileInfo = {
                            url: getStoredImageDownloadUrl(this.$store.state.requestURL, companyLogo),
                            innerUUID: companyLogo.innerUUID
                        };
                        this.haveLogo = 1;
                        localStorage.setItem('logoPath', companyLogo.imagePath);
                    } else {
                        this.haveLogo = 0;
                        this.fileInfo = {};
                        localStorage.setItem('logoPath', "");
                    }

                    const storedLoginInformation = JSON.parse(localStorage.getItem("loginInformation"));
                    if (storedLoginInformation == null || storedLoginInformation.objectData == null) {
                        throw new Error('Stored login information is missing');
                    }
                    storedLoginInformation.objectData.expiresTime = this.loginInformation.expiresTime;
                    storedLoginInformation.objectData.iapStatus = this.loginInformation.iapStatus;
                    storedLoginInformation.objectData.planType = this.loginInformation.planType;
                    localStorage.setItem('loginInformation', JSON.stringify(storedLoginInformation));
                    this.accountLoaded = true;
                } catch {
                    this.loadError = this.$t('common.loadFailed');
                } finally {
                    this.loading = false;
                }
            },
            ///////////////上传///////////////
            handleRemove() {
                if (!this.accountLoaded || this.deleteLogoStatus) {
                    return;
                }
                let that = this;
                this.deleteLogoStatus = true;
                let companyLogoUUID = that.fileInfo.innerUUID;
                if (companyLogoUUID == null || companyLogoUUID === "") {
                    that.deleteLogoStatus = false;
                    this.$message({
                        type: 'error',
                        message: this.$t('account.logoUuidMissing'),
                        duration: 6000
                    });
                    return;
                }
                this.$axios.post('update_company', {
                    params: {
                        companyLogoUUIDArrayKey: "",
                        innerUUID: that.userCompany.innerUUID,
                        companyUUID: that.userCompany.innerUUID,
                        accessDate: timest()
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$axios.post('update_company_logo', {
                            params: {
                                innerUUID: companyLogoUUID,
                                companyUUID: that.userCompany.innerUUID,
                                syncStatus: 1
                            }
                        }).then(response => {
                            if (response.data.errorCode === 0) {
                                this.$message({
                                    type: 'info',
                                    message: this.$t('common.deleteSuccess'),
                                    duration: 6000
                                });
                                that.userCompany.companyLogoUUIDArrayKey = "";
                                replaceStoredCompany(that.userCompany);
                                localStorage.setItem('logoPath', "");
                                that.haveLogo = 0;
                                that.deleteLogoStatus = false;
                                this.fileInfo = {}
                            } else {
                                that.deleteLogoStatus = false;
                                this.$message({
                                    type: 'error',
                                    message: this.$t('common.deleteFailed'),
                                    duration: 6000
                                });
                            }
                        }).catch(() => {
                            that.deleteLogoStatus = false;
                            this.$message({
                                type: 'error',
                                message: this.$t('common.deleteFailed'),
                                duration: 6000
                            });
                        })

                    } else {
                        that.deleteLogoStatus = false;
                        this.$message({
                            type: 'error',
                            message: this.$t('common.deleteFailed'),
                            duration: 6000
                        });
                    }
                }).catch(() => {
                    that.deleteLogoStatus = false;
                    this.$message({
                        type: 'error',
                        message: this.$t('common.deleteFailed'),
                        duration: 6000
                    });
                })
            },
            onBeforeUpload(file) {
                if (!this.accountLoaded || this.attachPhotosStatus) {
                    return false;
                }
                prepareFileUpload(this.uploadData, this.uploadFileMap, file, COMPANY_LOGO_FOLDER);
                this.uploadData.createDate = timest();
                if (this.haveLogo == 1) {
                    this.uploadData.oldCompanyLogoUUID = this.fileInfo.innerUUID;
                } else {
                    delete this.uploadData.oldCompanyLogoUUID;
                }
                this.addLogoText = this.$t('account.uploading');
                this.attachPhotosStatus = true;
            },
            showLogoUploadError(message) {
                this.addLogoText = "";
                this.attachPhotosStatus = false;
                this.$message({
                    type: 'error',
                    message: message,
                    duration: 6000
                });
            },
            reportLogoUploadError(error, file, operation) {
                reportHandledError(error, {
                    level: 'error',
                    module: 'file_upload',
                    operation: operation,
                    tags: {
                        file_type: 'company_logo'
                    },
                    context: {
                        fileName: file == null ? '' : file.name,
                        fileSize: file == null ? 0 : file.size,
                        folderName: COMPANY_LOGO_FOLDER
                    }
                });
            },
            handleUploadError(error, file) {
                discardPreparedFileUpload(this.uploadFileMap, file);
                this.reportLogoUploadError(error, file, 'company_logo_upload_request');
                this.showLogoUploadError(this.$t('account.logoUploadFailed'));
            },
            handlePictureCardPreview(file) {
                this.dialogImageUrl = file.url;
                this.dialogVisible = true;
            },
            handleSuccess(res, file) {
                this.addLogoText = "";
                this.attachPhotosStatus = false;
                if (res == null || Number(res.errorCode) !== 0 || !Array.isArray(res.uploadFileList) || res.uploadFileList.length !== 1) {
                    discardPreparedFileUpload(this.uploadFileMap, file);
                    this.reportLogoUploadError(new Error(res == null || res.errorMsg == null ? 'company logo upload failed' : res.errorMsg), file, 'company_logo_upload_response');
                    this.showLogoUploadError(this.$t('account.logoUploadFailed'));
                    return;
                }
                const innerUUID = takeUploadInnerUUID(this.uploadFileMap, file);
                const remoteKey = getUploadRemoteKey(COMPANY_LOGO_FOLDER, innerUUID);
                this.haveLogo = 1;
                this.fileInfo = {
                    name: innerUUID,
                    url: getDownloadV2Url(this.$store.state.requestURL, remoteKey),
                    innerUUID: innerUUID,
                    remoteKey: remoteKey
                };
                this.userCompany.companyLogoUUIDArrayKey = [innerUUID];
                replaceStoredCompany(this.userCompany);
                localStorage.setItem('logoPath', res.uploadFileList[0].url);
                this.$message({
                    type: 'info',
                    message: this.$t('common.uploadSuccess'),
                    duration: 6000
                });
            },
            handleConnectGoogle() {
                this.handleConnectSocial(PROVIDER_GOOGLE, signInWithGoogle, 'googleBinding', 'googleBound');
            },
            handleConnectApple() {
                this.handleConnectSocial(PROVIDER_APPLE, signInWithApple, 'appleBinding', 'appleBound');
            },
            handleConnectSocial(provider, signInFn, bindingKey, boundKey) {
                let self = this;
                if (!this.accountLoaded || this[bindingKey] || this[boundKey] !== null) {
                    return;
                }
                this[bindingKey] = true;
                signInFn().then(function (result) {
                    return bindLoggedInSocialLogin(self.$axios, {
                        provider: provider,
                        idToken: result.idToken,
                        rawNonce: result.rawNonce
                    });
                }).then(function (response) {
                    self[bindingKey] = false;
                    if (response.data.errorCode === 0) {
                        // Bound; prefer the social email returned by the backend, else "" (bound, email unknown).
                        const obj = response.data.objectData || {};
                        const email = provider === PROVIDER_GOOGLE ? obj.googleBound : obj.appleBound;
                        self[boundKey] = email != null ? email : "";
                        self.$message({
                            type: 'success',
                            message: provider === PROVIDER_GOOGLE ? self.$t('account.googleConnectedToast') : self.$t('account.appleConnectedToast'),
                            duration: 2000
                        });
                    } else if (response.data.errorCode === 225) {
                        // This provider is already bound to the current account.
                        self[boundKey] = "";
                        self.$message.error(self.$t('common.saveFailed'));
                    } else {
                        self.$message.error(self.$t('common.saveFailed'));
                    }
                }).catch(function () {
                    self[bindingKey] = false;
                    self.$message.error(self.$t('common.systemError'));
                });
            }
        },
        computed: {
            currentPlan() {
                return getCurrentPlanName(this.loginInformation);
            },
            isSubscriptionExpired() {
                return checkSubscriptionExpired(this.loginInformation);
            },
            subscriptionButtonText() {
                return this.currentPlan === "Free" ? this.$t('account.upgradeToPro') : this.$t('account.viewPlans');
            },
            googleLabel() {
                if (this.googleBinding) {
                    return this.$t('account.connecting');
                }
                if (this.googleBound === null) {
                    return this.$t('account.connectGoogle');
                }
                return this.googleBound ? this.$t('account.googleConnectedWithEmail', {email: this.googleBound}) : this.$t('account.googleConnected');
            },
            appleLabel() {
                if (this.appleBinding) {
                    return this.$t('account.connecting');
                }
                if (this.appleBound === null) {
                    return this.$t('account.connectApple');
                }
                return this.appleBound ? this.$t('account.appleConnectedWithEmail', {email: this.appleBound}) : this.$t('account.appleConnected');
            }
        },
        data() {
            return {
                deleteLogoStatus: false,
                loginInformation: {},
                userCompany: {},
                loading: true,
                accountLoaded: false,
                loadError: '',
                requestURL: "",
                attachPhotosStatus: false,
                uploadData: {
                    email: "",
                    needUpdateCompanyLogo: 1,
                    createDate: timest()
                },
                uploadFileMap: {},
                fileInfo: {},
                fullRequestURL: "",
                haveLogo: 0,
                addLogoText: "",
                loadImgPic: "@/static/loading.svg",
                googleBinding: false,
                googleBound: null,
                appleBinding: false,
                appleBound: null
            }
        },
        mounted() {
            this.queryUserInformation();

            this.fullRequestURL = getFileUploadUrl(this.$store.state.requestURL);

            // Pre-render the hidden Google Sign-In button for the Connect section.
            initGoogleSignIn();
        }
    }
</script>
<style scoped>
    .account-page {
        background: #f7f8fa;
        color: #263241;
        font-weight: 400;
        letter-spacing: 0;
    }

    .account-page > .account-header {
        height: 96px !important;
        padding: 28px 32px 24px;
        background: transparent;
    }

    .account-header h1 {
        margin: 0;
        font-size: 28px;
        font-weight: 400;
        line-height: 40px;
    }

    .account-page > .account-main {
        padding: 0 32px 40px;
        background: transparent;
    }

    .account-initial-loading {
        min-height: 240px;
    }

    .account-page .account-card {
        min-width: 0;
        max-width: 1120px;
        border: 1px solid #e1e6ed;
        border-radius: 16px;
        background: #ffffff;
        box-shadow: 0 2px 8px rgba(24, 39, 62, 0.025);
    }

    .account-page .account-card >>> .el-card__body {
        padding: 0;
    }

    .account-identity {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 32px 36px;
        border-bottom: 1px solid #edf0f4;
    }

    .profile-details {
        flex: 1;
        min-width: 0;
    }

    .logo-control {
        flex: 0 0 120px;
    }

    .connect-title {
        margin: 0 0 20px;
        color: #364152;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
    }

    .account-email {
        color: #727d8b;
        font-size: 15px;
        font-weight: 400;
        line-height: 24px;
        overflow-wrap: anywhere;
    }

    .plan-note {
        margin-top: 8px;
        color: #727d8b;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
    }

    .account-settings {
        display: block;
        padding: 32px 36px;
    }

    .connect-section {
        min-width: 0;
    }

    .connect-row {
        display: grid;
        gap: 16px;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .connect-button {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        min-height: 48px;
        padding: 12px 16px;
        border: 1px solid #e0e6ee;
        border-radius: 8px;
        background: #ffffff;
        color: #364152;
        font-family: inherit;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        text-align: left;
        cursor: pointer;
        transition: background 160ms ease, border-color 160ms ease;
    }

    .connect-button:hover:not(:disabled) {
        background: #f7f9fc;
        border-color: #b9c6d8;
    }

    .connect-button:disabled {
        cursor: default;
    }

    .connect-button.is-bound {
        background: #fafbfc;
    }

    .connect-logo {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
    }

    .connect-label {
        flex: 1;
        min-width: 0;
        overflow-wrap: anywhere;
    }

    .connect-state {
        width: 16px;
        height: 16px;
        flex: 0 0 16px;
        color: #8b95a3;
    }

    .connect-state.is-connected {
        color: #27815b;
    }

    .account-company-name {
        color: #364152;
        font-size: 22px;
        font-weight: 400;
        line-height: 32px;
        overflow-wrap: anywhere;
    }

    .account-page .logo-row {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .logo-preview {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 120px;
        height: 96px;
        padding: 12px;
        box-sizing: border-box;
        overflow: hidden;
        border: 1px solid #e2e6ec;
        border-radius: 8px;
        background: #ffffff;
    }

    .logo-preview >>> .el-image {
        width: 100%;
        height: 100%;
    }

    .logo-preview >>> img {
        object-fit: contain;
    }

    .logo-image-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        min-height: 72px;
        color: #a65050;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
    }

    .logo-image-error i {
        font-size: 18px;
    }

    .account-page .logo-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
    }

    .account-page .logo-actions .el-button {
        min-height: 32px;
        padding: 8px 0;
        border: 0;
        background: transparent;
        color: #126fe0;
        font-size: 13px;
        font-weight: 400;
    }

    .account-page .logo-actions .el-button:hover {
        color: #1065cd;
        background: transparent;
    }

    .account-page .logo-actions .logo-delete-button {
        color: #ac5050;
    }

    .avatar-uploader,
    .avatar-uploader >>> .el-upload {
        display: block;
        width: 120px;
    }

    .logo-upload-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        width: 120px;
        min-height: 108px;
        padding: 16px;
        box-sizing: border-box;
        border: 1px dashed #c8d3e1;
        border-radius: 8px;
        background: #fafbfd;
        color: #61748d;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
        cursor: pointer;
        transition: background 160ms ease, border-color 160ms ease;
    }

    .logo-upload-box:hover {
        border-color: #8ca8cd;
        background: #f5f8fc;
    }

    .avatar-uploader-icon {
        color: #7e8da1;
        font-size: 20px;
    }

    .account-plan-section {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 32px;
        padding: 28px 36px;
        border-top: 1px solid #e5ebf3;
        background: #f8fafd;
    }

    .plan-summary-line {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .current-plan-name {
        color: #263241;
        font-size: 20px;
        font-weight: 400;
        line-height: 28px;
        overflow-wrap: anywhere;
    }

    .plan-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 20px;
        color: #657184;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
        font-variant-numeric: tabular-nums;
    }

    .plan-expiry {
        font-weight: 600;
    }

    .plan-meta:empty {
        display: none;
    }

    .plan-share-note {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    .plan-share-note::before {
        content: '';
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #27815b;
    }

    .plan-expired-note {
        margin-top: 8px;
        color: #ac5050;
        font-size: 13px;
        font-weight: 400;
        line-height: 20px;
    }

    .account-page .subscriptionButton {
        min-height: 44px;
        margin: 0;
        padding: 12px 20px;
        border-color: #126fe0;
        border-radius: 8px;
        background: #126fe0;
        color: #ffffff;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        box-shadow: none;
        transition: background 160ms ease, border-color 160ms ease;
    }

    .account-page .subscriptionButton:hover,
    .account-page .subscriptionButton:focus {
        border-color: #1065cd;
        background: #1065cd;
    }

    @media (max-width: 760px) {
        .account-page > .account-header {
            height: 88px !important;
            padding: 24px 20px;
        }

        .account-page > .account-main {
            padding: 0 16px 32px;
        }

        .account-identity {
            padding: 24px;
            gap: 16px;
        }

        .account-settings {
            padding: 24px;
        }

        .account-plan-section {
            grid-template-columns: minmax(0, 1fr);
            gap: 20px;
            padding: 24px;
        }

        .account-page .subscriptionButton {
            justify-self: start;
        }

        .account-company-name {
            font-size: 18px;
            line-height: 28px;
        }

        .connect-row {
            grid-template-columns: minmax(0, 1fr);
            gap: 12px;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .connect-button,
        .logo-upload-box,
        .account-page .subscriptionButton {
            transition: none;
        }
    }
</style>
